import React from "react";

import { config } from "@/config/base";
import { useNavigate } from "react-router-dom";

import PasswordResetSuccessWidget from "@/components/widget/password-reset-success";

// Constants
const RESUME_ADDRESS = import.meta.env.VITE_RESUME_ADDRESS;

const STYLES = {
  title: {
    fontFamily: '"IranNastaliq", "B Nazanin", "Iranian Sans", serif',
    color: "#002248",
    fontSize: "65px"
  },
  footerText: {
    color: "#000000"
  }
} as const;

const PasswordResetSuccessPage: React.FC = () => {
  const navigate = useNavigate();

  const handleConfirm = () => {
    navigate("/dashboard/login");
  };

  return (
    <div
      className="relative flex min-h-screen w-full items-center justify-center bg-cover bg-center p-4"
      style={{ backgroundImage: `url(${config.login.background})` }}
    >
      <Overlay />
      <SuccessCard onConfirm={handleConfirm} />
    </div>
  );
};

const Overlay = () => (
  <div
    className="absolute inset-0"
    style={{ backgroundColor: "rgba(27, 27, 27, 0.5)" }}
  />
);

interface SuccessCardProps {
  onConfirm: () => void;
}

const SuccessCard = ({ onConfirm }: SuccessCardProps) => (
  <div
    className="relative z-10 flex w-full max-w-[450px] flex-col"
    style={{ minHeight: "440px" }}
  >
    <MainCard>
      <LogoSection />
      <ContentSection onConfirm={onConfirm} />
    </MainCard>
    <Footer />
  </div>
);

const MainCard = ({ children }: { children: React.ReactNode }) => (
  <div
    className="relative flex-1 bg-white px-6 py-8 sm:px-12 sm:py-10"
    style={{ borderRadius: "20px" }}
  >
    {children}
  </div>
);

const LogoSection = () => (
  <div className="mb-4 flex flex-row-reverse items-center justify-end gap-3 sm:mb-8 sm:gap-4">
    <h1
      className="text-center text-3xl font-normal leading-tight sm:text-5xl sm:leading-[81px]"
      style={STYLES.title}
    >
      {config.appConfig.texts.organizationName}
    </h1>
    <img
      src={config.login.logo}
      alt={config.appConfig.texts.logoAlt}
      width={80}
      height={80}
      className="h-20 w-20 rounded-full sm:h-24 sm:w-24"
      style={{ filter: "drop-shadow(0px 10px 10px rgba(0, 0, 0, 0.25))" }}
    />
  </div>
);

interface ContentSectionProps {
  onConfirm: () => void;
}

const ContentSection = ({ onConfirm }: ContentSectionProps) => (
  <div className="mx-auto w-full max-w-[348px]">
    <PasswordResetSuccessWidget onConfirm={onConfirm} />
  </div>
);

const Footer = () => (
  <div
    className="flex flex-col items-center justify-center gap-2 px-4 py-4 sm:gap-3 sm:py-5"
    style={{
      background: "#F2F2F2",
      borderRadius: "0 0 20px 20px",
      minHeight: "87px"
    }}
  >
    {RESUME_ADDRESS && <ResumeLink />}
  </div>
);

const ResumeLink = () => (
  <a
    href={RESUME_ADDRESS}
    target="_blank"
    rel="noopener noreferrer"
    className="text-center font-normal transition-opacity hover:opacity-80 sm:text-sm"
    style={STYLES.footerText}
  >
    {config.appConfig.texts.loginSystemLink}{" "}
    <span className="mx-1 font-bold">{config.appConfig.texts.fullProfileTitle}</span>
  </a>
);

export default PasswordResetSuccessPage;
